import { OrderStatus } from '@/components/order/order.types';
import {
  ApprovalsTableCell,
  DoctorNoteTableCell,
  PrescriptionsTableCell,
  ProductNameWithQuantityTableCell,
  ShipmentStatusTableCell,
} from '@/components/orders/tables/cells/PharmacistOrdersTableCell';
import OrderTableTitleAdornment from '@/components/orders/tables/delivery-tables/OrderTableTitleAdornment';
import SelectionHeaderCell from '@/components/orders/tables/delivery-tables/cells/OrderSelectionHeaderCell';
import OrderSelectionTableCell from '@/components/orders/tables/delivery-tables/cells/OrderSelectionTableCell';
import { FF_ENABLE_PROCESSING_DISPENSE_NOTE } from '@/constants/featureFlags';
import type { ListOrdersResponse } from '@/context/orders/DeliveryTableFilteredData';
import { useDeliveryTableData } from '@/context/orders/DeliveryTableFilteredData';
import { useMarkAsCollectedModal, usePharmacistSplitOrderModal } from '@/context/orders/PharmacistOrdersModals';
import { ListOrdersView } from '@/hooks/orders/useListOrders';
import { useOrdersPageSizeOptions } from '@/hooks/orders/useOrdersPageSizeOptions';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { formatISODateHelper } from '@/utils/helpers';
import { dateColumnSort } from '@/utils/table';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared/constants/shippingOptions';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Box, Button, PaginationVariant, Table, Tooltip } from '@montugroup/design-system';
import { Skeleton } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import CollapsibleTableWrapper from '../delivery-tables/CollapsibleTableWrapper';
import type TableInfoDialog from './TableInfoDialog';

type Props = {
  shippingType: Exclude<(typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'], 'ThreeHour'>;
  title?: string;
  tooltipProps?: ComponentProps<typeof TableInfoDialog>;
};

export type ShippingTypeSpecificOrder<T extends keyof ListOrdersResponse> = ArrayElement<
  NonNullable<ListOrdersResponse[T]>
>;

const SAME_DAY_DISPENSE_BY = '3pm';
const SAME_DAY_AFTERNOON_DISPENSE_BY = '12pm';

const TableTitles: Record<Props['shippingType'], string> = {
  [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].name]: 'Same day',
  [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].name]: 'Same day',
  [DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name]: 'Click & Collect',
  [DELIVERY_OPTIONS[DELIVERY_OPTION.Standard].name]: 'Standard',
};

function DeliveryTable({ shippingType, title, tooltipProps }: Props) {
  const { flags } = useFeatureFlags();
  const { data, isLoading, view } = useDeliveryTableData();
  const { showSplitOrderModal } = usePharmacistSplitOrderModal();
  const { showMarkAsCollectedModal } = useMarkAsCollectedModal();
  const pageSizeOptions = useOrdersPageSizeOptions();
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
  } = useTableManualPagination({
    page: 0,
    pageSize: 10,
  });

  const { orders, pageOrders } = useMemo(() => {
    const orders = data?.[shippingType] ?? [];
    const start = page * pageSize;

    return {
      orders,
      pageOrders: orders.slice(start, start + pageSize),
    };
  }, [data, shippingType, page, pageSize]);

  type CurrentOrder = ShippingTypeSpecificOrder<typeof shippingType>;

  const columns: ColumnDef<CurrentOrder>[] = useMemo(
    () => [
      {
        accessorKey: 'orderCode',
        header: 'Order ID',
        enableSorting: true,
      },
      {
        accessorKey: 'orderPlaced',
        header: 'Order Placed',
        enableSorting: true,
        accessorFn: (originalRow) => formatISODateHelper(originalRow.orderPlaced),
        sortingFn: (rowA, rowB) => dateColumnSort(rowA.original.orderPlaced, rowB.original.orderPlaced),
      },
      {
        accessorKey: 'patientCode',
        header: 'Patient ID',
        accessorFn: (originalRow) => originalRow.patient.patientCode,
      },
      {
        accessorKey: 'patientName',
        header: 'Patient',
        accessorFn: (originalRow) => originalRow.patient.name,
      },
      {
        accessorKey: 'approvals',
        header: 'Approvals',
        enableSorting: false,
        cell: ({ row }) => (
          <Box sx={{ textAlign: 'center' }}>
            <ApprovalsTableCell patientCode={row.original.patient.patientCode} orderId={row.original.orderId} />
          </Box>
        ),
      },
      {
        accessorKey: 'prescriptions',
        header: 'Prescriptions',
        enableSorting: false,
        cell: ({ row }) => <PrescriptionsTableCell prescriptions={row.original.prescriptions} />,
      },
      {
        accessorKey: 'orderProducts',
        header: 'Products',
        enableSorting: false,
        cell: ({ row }) => (
          <ProductNameWithQuantityTableCell
            productNamesAndQuantities={row.original.orderProducts.map(({ product, quantity, repeats }) => ({
              id: `${row.original.orderId}-${product.id}`,
              name: product.name,
              quantity,
              repeats,
            }))}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <ShipmentStatusTableCell
            status={row.original.status}
            orderId={row.original.orderId}
            deliveryOption={shippingType}
          />
        ),
      },
      ...(view === ListOrdersView.PendingCollection
        ? ([
            {
              enableSorting: false,
              id: 'markAsCollected',
              header: 'Mark as collected',
              cell: ({ row }) => {
                if (row.original.status === 'Dispensed') {
                  const MarkAsCollectedButton = () => (
                    <Button
                      aria-label="Mark as collected"
                      variant="outlined"
                      size="small"
                      disabled={!row.original.isLabelPrinted}
                      onClick={() => showMarkAsCollectedModal({ orderIds: [row.original.orderId] })}
                    >
                      Mark as collected
                    </Button>
                  );

                  if (!row.original.isLabelPrinted) {
                    return (
                      <Tooltip title={'Shipping label not printed'} arrow>
                        <span>
                          <MarkAsCollectedButton />
                        </span>
                      </Tooltip>
                    );
                  }

                  return <MarkAsCollectedButton />;
                }

                return null;
              },
            },
          ] as ColumnDef<CurrentOrder>[])
        : []),
      ...(shippingType === DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].name
        ? [
            {
              accessorKey: '',
              header: 'Dispense by',
              cell: () => SAME_DAY_DISPENSE_BY,
            },
          ]
        : []),
      ...(shippingType === DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].name
        ? [
            {
              accessorKey: '',
              header: 'Dispense by',
              cell: () => SAME_DAY_AFTERNOON_DISPENSE_BY,
            },
          ]
        : []),
      ...(flags[FF_ENABLE_PROCESSING_DISPENSE_NOTE] && view === ListOrdersView.ProcessingDispense
        ? ([
            {
              accessorKey: '',
              id: 'doctorNote',
              header: 'Doctor Note',
              enableSorting: false,
              cell: ({ row }) => (
                <DoctorNoteTableCell
                  prescriptions={row.original.prescriptions.map(({ code, path, dispenseNote }) => ({
                    name: code,
                    path,
                    dispense_note: dispenseNote ?? '',
                  }))}
                />
              ),
            },
          ] as ColumnDef<CurrentOrder>[])
        : []),
      ...(view === ListOrdersView.ProcessingDispense
        ? ([
            {
              enableSorting: false,
              id: 'unableToDispense',
              cell: ({ row }) => {
                if (row.original.status === 'Processing Dispense') {
                  return (
                    <Button
                      aria-label="Unable to dispense"
                      variant="outlined"
                      size="small"
                      onClick={() => showSplitOrderModal(row.original)}
                    >
                      Unable to dispense
                    </Button>
                  );
                }
                return null;
              },
            },
          ] as ColumnDef<CurrentOrder>[])
        : []),

      {
        accessorKey: '',
        id: 'select',
        header: () => (
          <SelectionHeaderCell
            orders={pageOrders.filter(
              // Out of stock, interval orders & C&C orders without a printed label arent selectable
              (o) =>
                o.status !== OrderStatus.PharmacyOutOfStock &&
                o.status !== OrderStatus.IntervalTime &&
                !(o.status === OrderStatus.Dispensed && !o.isLabelPrinted) &&
                o.status !== OrderStatus.AwaitingShipment,
            )}
          />
        ),
        enableSorting: false,
        cell: ({ row }) => (
          <OrderSelectionTableCell
            id={row.original.orderId}
            disabled={
              row.original.status === OrderStatus.PharmacyOutOfStock ||
              row.original.status === OrderStatus.IntervalTime ||
              (row.original.status === OrderStatus.Dispensed && !row.original.isLabelPrinted) ||
              row.original.status === OrderStatus.AwaitingShipment
            }
          />
        ),
      },
    ],
    [view, shippingType, flags, showMarkAsCollectedModal, showSplitOrderModal, pageOrders],
  );

  // Rendering a custom loading state here (instead of utilising the Table) as we want to
  // collapse the wrapping accordion if there are no results, doing it this way isn't jarring to users.
  if (isLoading) {
    return (
      <Skeleton width="100%" height={38} variant="rectangular" sx={{ marginBottom: '0.5rem' }} data-testid="loading" />
    );
  }

  if (data && !data[shippingType]) {
    return null;
  }

  return (
    <CollapsibleTableWrapper
      title={title || TableTitles[shippingType]}
      defaultExpanded={orders.length > 0}
      TitleAdornment={
        <OrderTableTitleAdornment table={shippingType} orderCount={orders.length} tooltipProps={tooltipProps} />
      }
    >
      <Table
        showPagination
        manualPagination
        shouldResetPageIndex={shouldResetPageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        total={orders.length}
        paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
        onPaginationModelChange={setPaginationModel}
        data={pageOrders}
        columns={columns}
      />
    </CollapsibleTableWrapper>
  );
}

export default DeliveryTable;
